import { createRoute, Link, Outlet, useNavigate } from "@tanstack/react-router";
import { type FC, type SVGProps } from "react";
import {
  AlertTriangle,
  Bell01,
  Building01,
  Building02,
  CreditCardX,
  File02,
  FileCheck01,
  Grid01,
  Key01,
  RefreshCcw01,
  SearchMd,
  Send01,
  Trash01,
  UploadCloud01,
  Users01,
  Wallet01,
} from "@untitledui/icons";
import { Route as RootRoute } from "../__root";
import { requireAdmin } from "../../lib/authGuard";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { NavAccountCard } from "@/components/application/app-navigation/base-components/nav-account-card";
import { ThemeToggle } from "../../components/custom/ThemeToggle";
import { SyncStatusBanner } from "../../components/custom/SyncStatusBanner";
import { useSyncProgressSse } from "../../hooks/useSyncProgressSse";
import { useCardsToInvalidatePendingCount } from "../../hooks/useMicrogroupQueries";
import { useAuthStore } from "../../lib/authStore";
import { apiCall } from "../../lib/apiClient";

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  id: "admin-layout",
  beforeLoad: ({ location }) => requireAdmin({ location: { href: location.href } }),
  component: AdminLayoutComponent,
});

interface AdminNavItem {
  to: string;
  label: string;
  icon: FC<SVGProps<SVGSVGElement>>;
}

interface AdminNavSection {
  title: string;
  items: AdminNavItem[];
}

const adminNav: AdminNavSection[] = [
  {
    title: "Operace",
    items: [
      { to: "/admin/dashboard", label: "Dashboard", icon: Grid01 },
      { to: "/admin/sync", label: "Synchronizace", icon: RefreshCcw01 },
      { to: "/admin/documents", label: "Dokumenty", icon: File02 },
      { to: "/admin/budgets", label: "Rozpočty", icon: Wallet01 },
      { to: "/admin/distribution", label: "Distribuce", icon: Send01 },
      { to: "/admin/anomalies", label: "Anomálie", icon: AlertTriangle },
      { to: "/admin/microgroup-requests", label: "Žádosti mikroskupin", icon: Users01 },
      { to: "/admin/user-invites", label: "Pozvánky", icon: Users01 },
      { to: "/admin/cards-to-invalidate", label: "Karty k invalidaci", icon: CreditCardX },
    ],
  },
  {
    title: "Finance",
    items: [
      { to: "/admin/payment-accounts", label: "FIO účty", icon: Building02 },
      { to: "/admin/tenants", label: "Tenanti", icon: Building01 },
      { to: "/admin/billing-groups", label: "Střediska", icon: Building01 },
      { to: "/admin/holders", label: "Držitelé", icon: Building02 },
      { to: "/admin/push-assist", label: "Push-assist", icon: Bell01 },
    ],
  },
  {
    title: "Compliance",
    items: [
      { to: "/admin/consent-uploads", label: "Paper consent", icon: UploadCloud01 },
      { to: "/admin/consent/versions", label: "Verze textu", icon: FileCheck01 },
      { to: "/admin/credentials/axigon", label: "Axigon credentials", icon: Key01 },
      { to: "/admin/gdpr-erasure", label: "GDPR výmaz", icon: Trash01 },
    ],
  },
];

function AdminLayoutComponent() {
  const { snapshot, zones } = useSyncProgressSse();
  const pending = useCardsToInvalidatePendingCount();
  const navigate = useNavigate();
  const auth = useAuthStore((s) => s.auth);
  const resetAuth = useAuthStore((s) => s.reset);

  const breachCount = pending.data?.breach_count ?? 0;
  const userEmail = auth.state === "authenticated" ? auth.user.email : "—";

  async function handleLogout() {
    try {
      await apiCall("/api/public/auth/logout", {
        method: "POST",
        body: JSON.stringify({}),
      });
    } catch {
      // Ignore — local state se vyresetuje a redirect proběhne bez ohledu
      // na server response.
    }
    resetAuth();
    void navigate({ to: "/" });
  }

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-[280px_1fr] md:grid-rows-[64px_1fr] [grid-template-areas:'topbar''main'] md:[grid-template-areas:'sidebar_topbar''sidebar_main'] bg-secondary">
      <aside
        aria-label="Hlavní navigace"
        className="hidden md:flex md:[grid-area:sidebar] md:row-span-2 flex-col bg-primary border-r border-secondary"
      >
        <div className="px-5 py-5 flex items-center gap-3">
          <div className="size-9 rounded-lg bg-brand-solid flex items-center justify-center text-white font-bold text-base">
            A
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-semibold text-primary text-sm">axigon</span>
            <span className="text-xs text-tertiary">admin</span>
          </div>
        </div>

        <div className="px-4 pb-3">
          <label htmlFor="admin-search" className="sr-only">
            Hledat
          </label>
          <div className="relative">
            <SearchMd
              aria-hidden="true"
              className="absolute left-3 top-1/2 -translate-y-1/2 size-4 shrink-0 text-quaternary"
            />
            <input
              id="admin-search"
              type="search"
              placeholder="Hledat…"
              className="w-full pl-9 pr-3 py-2 bg-primary text-primary border border-secondary rounded-lg text-sm placeholder:text-quaternary focus:outline-2 focus:outline-brand"
            />
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-5">
          {adminNav.map((section) => (
            <div key={section.title}>
              <div className="px-3 mb-1 text-[11px] font-semibold tracking-wider text-quaternary uppercase">
                {section.title}
              </div>
              <ul className="space-y-0.5 list-none p-0 m-0">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        activeProps={{ "aria-current": "page" }}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-tertiary no-underline hover:bg-secondary hover:text-primary aria-[current=page]:bg-secondary aria-[current=page]:text-primary aria-[current=page]:font-semibold"
                      >
                        <Icon aria-hidden="true" className="size-5 shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="border-t border-secondary p-3" data-testid="admin-account">
          <NavAccountCard
            selectedAccountId="me"
            items={[
              {
                id: "me",
                name: userEmail,
                email: "Globální admin",
                avatar: "",
                status: "online",
              },
            ]}
            onSignOut={() => void handleLogout()}
            menuTop={
              <div className="flex flex-col gap-1.5 p-3">
                <p className="px-0.5 text-xs font-semibold text-tertiary">Vzhled</p>
                <ThemeToggle className="w-full" />
              </div>
            }
          />
        </div>
      </aside>

      <header className="[grid-area:topbar] flex items-center justify-between gap-4 bg-primary border-b border-secondary px-6">
        <h1 className="text-xl font-semibold m-0 text-primary">Administrace</h1>
        <div className="flex items-center gap-2">
          <SyncStatusBanner snapshot={snapshot} zones={zones} />
        </div>
      </header>

      <main id="main" className="[grid-area:main] overflow-y-auto bg-secondary p-6">
        {breachCount > 0 && (
          <div className="mb-6">
            <InlineAlert
              variant="error"
              title={`${breachCount} karet pending invalidation > 48 h`}
              data-testid="cards-invalidation-critical-banner"
            >
              Riziko: ex-member tankování stále možné. Otevřete{" "}
              <Link
                to="/admin/cards-to-invalidate"
                className="font-semibold text-brand-secondary hover:underline"
              >
                frontu karet
              </Link>{" "}
              a zneplatněte je v Axigonu co nejdříve.
            </InlineAlert>
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
